import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  SidebarDietPlan,
  SidebarExercise,
  SidebarSleepCoach,
  SidebarMindfulness,
} from "@utils/defaultImages";
import { getNotifications, seenNotifications } from "@api/notification";
import InfiniteScroll from "react-infinite-scroll-component";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

const Notification = ({ onUnreadCountChange, onOpened, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch initial notifications
  const fetchInitialNotifications = useCallback(async () => {
    try {
      setIsInitialLoading(true);
      setError(null);
      const data = await getNotifications(1, 15);
      const response = data?.data;

      console.log(response);
      if (response) {
        const notificationsData = response?.data || [];
        const currentPageNum = response?.current_page || 1;
        const lastPageNum = response?.last_page || 1;
        const unreadCountFromAPI = data?.unread_count || 0;


        if (Array.isArray(notificationsData)) {
          setNotifications(notificationsData);
        } else if (Array.isArray(notificationsData?.data)) {
          setNotifications(notificationsData.data);
        } else {
          setNotifications([]);
        }

        setUnreadCount(unreadCountFromAPI);
        onUnreadCountChange?.(unreadCountFromAPI);
        onOpened?.();

        setHasMore(currentPageNum < lastPageNum);
        setCurrentPage(currentPageNum + 1);

        // Call API to mark notifications as seen only if there are unread notifications
        if (unreadCountFromAPI > 0) {
          try {
            await seenNotifications();
            // API is marked as seen, but UI will only update when notification bar is reopened
          } catch (error) {
            console.error("Error marking notifications as seen:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching initial notifications:", error);
      setError(error.response?.data?.message || "An error occurred");
    } finally {
      setIsInitialLoading(false);
    }
  }, [onOpened, onUnreadCountChange]);

  // Fetch more notifications for pagination
  const fetchMoreNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getNotifications(currentPage, 15);
      if (response) {
        const notificationsData = response?.data || [];
        const currentPageNum = response?.current_page || currentPage;
        const lastPageNum = response?.last_page || currentPage;

        if (Array.isArray(notificationsData)) {
          setNotifications((prev) => [...prev, ...notificationsData]);
        } else if (Array.isArray(notificationsData?.data)) {
          setNotifications((prev) => [...prev, ...notificationsData.data]);
        }
        
        setHasMore(currentPageNum < lastPageNum);
        setCurrentPage(currentPageNum + 1);
      }
    } catch (error) {
      console.error("Error fetching more notifications:", error);
      setError(error.response?.data?.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  console.log(currentPage);

  // Load initial notifications when component mounts - only once
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchInitialNotifications();
    }
  }, []);

  // Function called when user scrolls to bottom
  const fetchMoreData = () => {
    if (hasMore && !isLoading) {
      fetchMoreNotifications();
    }
  };

  const handleNotificationClick = (notification) => {
    const nextUnreadCount = notification.is_read
      ? unreadCount
      : Math.max(0, unreadCount - 1);

    if (!notification.is_read) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(nextUnreadCount);
      onUnreadCountChange?.(nextUnreadCount);
    }

    onClose?.();
    handleNavigation(notification);
  };

  const handleNavigation = (notification) => {
    const { module } = notification;
    const route = [
      "/diet-plan",
      "/exercise-plan",
      "/sleep-coach",
      "/mindfulness",
    ].includes(module)
      ? module
      : "/";

    navigate(route);
  };

  // Format date function
  const formatTime = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      
      // Check if the date is today
      const isToday = date.toDateString() === now.toDateString();
      
      if (isToday) {
        // For today: show relative time
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
        
        if (diffInHours < 1) {
          const diffInMinutes = Math.floor((now - date) / (1000 * 60));
          return `${diffInMinutes} min ago`;
        }
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      }
      
      // For previous days: show date and time
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      return `${formattedDate} at ${formattedTime}`;
    } catch (error) {
      return "";
    }
  };

  // Helper function to pick the right icon based on the module path
  const getIcon = (module) => {
    switch (module) {
      case "/diet-plan":
        return <SidebarDietPlan className="w-6 h-6 text-white" />;
      case "/exercise-plan":
        return <SidebarExercise className="w-6 h-6 text-white" />;
      case "/sleep-coach":
        return <SidebarSleepCoach className="w-6 h-6 text-white" />;
      case "/mindfulness":
        return <SidebarMindfulness className="w-6 h-6 text-white" />;
      default:
        return <SidebarDietPlan className="w-6 h-6 text-white" />;
    }
  };

  return (
    <div className="absolute right-0 z-[9999999]">
      <div className="w-full min-w-[300px] max-w-[300px] md:min-w-[500px] md:max-w-[500px] bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-gray-500 text-sm font-medium">
            {t("Notifications")}
          </h2>
          {unreadCount > 0 && (
            <span className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white text-xs px-2 py-1 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>

        {/* Scrollable Area with Infinite Scroll */}
        <div
          id="scrollable-notification-container"
          className="max-h-[500px] overflow-y-auto custom-scrollbar"
        >
          {isInitialLoading ? (
            <div className="p-8 text-center text-gray-400">
              <CircularProgress thickness={7} size={30} />
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-400">
              {error}
              <button
                onClick={() => fetchInitialNotifications()}
                className="mt-2 text-blue-500 underline block mx-auto"
              >
                {t("Try again")}
              </button>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={notifications.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <div className="p-4 text-center text-gray-400">
                  <CircularProgress thickness={7} size={24} />
                </div>
              }
              endMessage={
                notifications.length > 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">
                    {t("No more notifications")}
                  </div>
                ) : null
              }
              scrollableTarget="scrollable-notification-container"
            >
              {notifications.length > 0 ? (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`flex items-start gap-4 p-4 transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${
                      !notif.is_read ? 'bg-blue-100' : 'hover:bg-gray-200'
                    }`}
                  >
                    {/* Icon Wrapper - Gradient background */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] flex items-center justify-center shadow-md">
                      {getIcon(notif.module)}
                    </div>

                    {/* Text Content */}
                    <div className="flex-1">
                      <div className="flex justify-between items-baseline">
                        <h3 className={`text-gray-900 text-[15px] ${!notif.is_read ? 'font-bold' : 'font-medium'}`}>
                          {notif.title}
                        </h3>
                        <span className="text-gray-400 text-xs ml-2 whitespace-nowrap">
                          {formatTime(notif.created_at)}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm mt-1 leading-snug">
                        {notif.message}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-400">
                  {t("No notifications yet")}
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Custom Scrollbar Styling */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: #f1f1f1;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(150deg, #0771ef 12.89%, #15aafc 85.22%);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(150deg, #0660d0 12.89%, #1295e0 85.22%);
        }
      `}</style>
    </div>
  );
};

export default Notification;